import { useMemo } from 'react';
import { dataSourceToDataUrl, selectContext } from '@icp/form-renderer-core';
import { useConditionalProperty } from './useConditionalProperty';
import useVariablePattern from './useVariablePattern';
import { useStore } from '../store';

/**
 * @param dataUrlProp
 * @param dataSource
 * @param dataUrlResolveMode - 控制 dataUrl 中 :variable 解析失败时的行为：
 *   - 'strict'（默认）：任何变量解析失败都不发请求（返回 null）
 *   - 'loose'：解析失败的变量忽略，使用剩余字符串发请求
 *   - 'smart'：path 部分变量解析失败不发请求；query 部分变量解析失败则移除该参数后继续发请求
 */
export default function useDataUrl({ dataUrlProp, dataSource, dataUrlResolveMode = 'strict' }) {
  const store = useStore();
  const context = selectContext(store.getState());

  const dataUrlConfig = dataUrlProp || dataSourceToDataUrl(dataSource, context);
  const dataUrlString = useConditionalProperty(dataUrlConfig);

  const isSmart = dataUrlResolveMode === 'smart';
  const isStrict = dataUrlResolveMode === 'strict';

  // 将 URL 拆分为 path 和 query 两部分，供 smart 模式使用
  const [pathPart, queryPart] = useMemo(() => {
    if (!isSmart || typeof dataUrlString !== 'string') {
      return [undefined, undefined];
    }

    const idx = dataUrlString.indexOf('?');
    if (idx === -1) {
      return [dataUrlString, undefined];
    }

    return [dataUrlString.slice(0, idx), dataUrlString.slice(idx + 1)];
  }, [isSmart, dataUrlString]);

  // strict/loose 模式：对完整 URL 做变量解析；smart 模式跳过
  const resolvedFull = useVariablePattern(isSmart ? undefined : dataUrlString, isStrict);
  // smart 模式：path 部分严格解析，query 部分宽松解析；非 smart 模式跳过
  const resolvedPath = useVariablePattern(pathPart, true);
  const resolvedQuery = useVariablePattern(queryPart, false);

  if (dataUrlResolveMode === 'smart') {
    if (resolvedPath === null) {
      return null;
    }

    if (resolvedQuery) {
      // 过滤掉值为空的 query 参数
      const params = new URLSearchParams(resolvedQuery);
      [...params.entries()].forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });
      const cleanedQuery = params.toString();
      return cleanedQuery ? `${resolvedPath}?${cleanedQuery}` : resolvedPath;
    }

    return resolvedPath;
  }

  return resolvedFull;
}
